'use client'

import { createContext, PropsWithChildren, useContext, useRef } from 'react'
import { useDebounceCallback, useEventListener } from 'usehooks-ts'

// Radius scales with viewport width: 20vw clamped to usable range.
// 1920px -> 384px, 1440px -> 288px, 1024px -> 205px
const RADIUS_SCALE = 0.2
const MIN_RADIUS = 150
const MAX_RADIUS = 500

type Entry = { el: HTMLElement; onNear: () => void; rect: DOMRect | null }

type CursorPrefetchRegistry = {
  register: (key: symbol, entry: Omit<Entry, 'rect'>) => void
  unregister: (key: symbol) => void
}

const CursorPrefetchContext = createContext<CursorPrefetchRegistry | null>(null)

export function useCursorPrefetchRegistry() {
  return useContext(CursorPrefetchContext)
}

/**
 * Provides a shared cursor-proximity registry for desktop card prefetching.
 *
 * One mousemove + one scroll listener for all cards. Cards register/unregister
 * via the context. Proximity checks run inline (no rAF).
 *
 * Card geometry is measured LAZILY: register() stores no rect, and the first
 * proximity check reads all pending rects in one batched pass. This keeps
 * getBoundingClientRect off the hydration critical path, where per-card reads
 * interleaved with React's DOM writes cause forced synchronous reflow.
 */
export function CursorPrefetchProvider({ children }: PropsWithChildren) {
  const registryRef = useRef(new Map<symbol, Entry>())
  const mouseRef = useRef({ x: 0, y: 0 })

  const documentRef = useRef<Document>(
    typeof document !== 'undefined' ? document : (null as unknown as Document),
  )

  // leading + maxWait = throttle: fires immediately, then at most every 32ms (~30fps)
  const checkProximity = useDebounceCallback(
    () => {
      if (registryRef.current.size === 0) return

      // Batched read pass: measure any not-yet-measured cards together. With no DOM writes
      // between reads this is a single reflow at interaction time, never the per-card thrash
      // that the old register-time read caused during hydration.
      for (const entry of registryRef.current.values()) {
        if (entry.rect === null) entry.rect = entry.el.getBoundingClientRect()
      }

      const { x: mx, y: my } = mouseRef.current

      const radius = Math.max(
        MIN_RADIUS,
        Math.min(MAX_RADIUS, Math.round(window.innerWidth * RADIUS_SCALE)),
      )

      const r2 = radius * radius

      for (const [key, entry] of registryRef.current) {
        const rect = entry.rect
        if (!rect) continue
        // AABB early exit
        if (
          rect.right < mx - radius ||
          rect.left > mx + radius ||
          rect.bottom < my - radius ||
          rect.top > my + radius
        )
          continue

        const dx = mx - (rect.left + rect.width / 2)
        const dy = my - (rect.top + rect.height / 2)
        if (dx * dx + dy * dy < r2) {
          entry.onNear()
          registryRef.current.delete(key)
        }
      }
    },
    32,
    { leading: true, trailing: true, maxWait: 32 },
  )

  useEventListener(
    'mousemove',
    (e: MouseEvent) => {
      mouseRef.current.x = e.clientX
      mouseRef.current.y = e.clientY
      checkProximity()
    },
    undefined,
    { passive: true },
  )

  // Scroll moves cards relative to stationary cursor — refresh cached rects then re-check.
  // Capture phase catches scroll on any scrollable element.
  const onScroll = () => {
    for (const entry of registryRef.current.values()) {
      entry.rect = entry.el.getBoundingClientRect()
    }
    checkProximity()
  }

  useEventListener('scroll', onScroll, documentRef, { capture: true, passive: true })

  return (
    <CursorPrefetchContext.Provider
      value={{
        register: (key, entry) => {
          // No layout read here — measured lazily on the first proximity check (see above).
          registryRef.current.set(key, { ...entry, rect: null })
        },
        unregister: (key) => {
          registryRef.current.delete(key)
        },
      }}
    >
      {children}
    </CursorPrefetchContext.Provider>
  )
}
